import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { LatestDocumentAssessmentResultByDocumentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetLatestDocumentAssessmentResultByDocumentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC document assessment result data to match the GraphQL query structure
 */
const mapTrpcDocumentAssessmentResultToGraphQL = (
  trpcData: LatestDocumentAssessmentResultByDocumentIdResponseRow[]
): GetLatestDocumentAssessmentResultByDocumentIdQuery => {
  return {
    document_assessment_result: trpcData,
  };
};

export const useGetLatestDocumentAssessmentResultByDocumentId = (
  documentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.latestDocumentAssessmentResultByDocumentId.queryOptions(
      {
        documentId,
      }
    ),

    enabled: trpcEnabled && !skip && !!documentId,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcDocumentAssessmentResultToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
